/**
 * Thank You Page - Post-submission confirmation
 * 
 * Displayed after a supplier successfully submits their survey response.
 * Accessed at /supplier-survey/:token/thank-you
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 

  CheckCircle, 
  Mail, 
  FileText, 
  Users, 
  Shield,
  Clock
} from 'lucide-react';
import { apiFetch } from '../../../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ThankYouPage() {
  const { token } = useParams();
  const [surveyName, setSurveyName] = useState('Supplier Data Survey');
  const [supplierName, setSupplierName] = useState('');

  useEffect(() => {
    // Optionally fetch survey details
    fetchSurveyInfo();
  }, [token]);

  const fetchSurveyInfo = async () => {
    try {
      const response = await fetch(`${API}/api/v1/supplier-portal/public/survey/${token}`);
      if (response.ok) {
        const data = await response.json();
        setSurveyName(data.survey?.surveyName || 'Supplier Data Survey');
        setSupplierName(data.supplier?.supplierName || '');
      }
    } catch (err) {
      // Silent fail - we already have defaults
      console.log('Could not fetch survey info');
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-4"
      data-testid="thank-you-page"
    >
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-12 text-center">
        {/* Success Icon */}
        <div className="mb-6">
          <div className="h-24 w-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Thank You!
          </h1>
          <p className="text-lg text-gray-600">
            Your responses have been successfully submitted
          </p>
        </div>

        {/* Confirmation Details */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Survey</p>
              <p className="font-medium text-gray-900">{surveyName}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">Submitted</p>
              <p className="font-medium text-gray-900">
                {new Date().toLocaleString()}
              </p>
            </div>
            {supplierName && (
              <>
                <div>
                  <p className="text-gray-500 mb-1">Supplier</p>
                  <p className="font-medium text-gray-900">{supplierName}</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Status</p>
                  <p className="font-medium text-green-600 flex items-center justify-center gap-1">
                    <CheckCircle size={14} />
                    Submitted
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* What Happens Next */}
        <div className="text-left bg-blue-50 rounded-xl border border-blue-200 p-6 mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-blue-600" size={18} />
            What Happens Next?
          </h3>
          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                1
              </span>
              <span>Your responses will be reviewed by our sustainability team</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                2
              </span>
              <span>We'll contact you if we need any clarification</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                3
              </span>
              <span>Your data will be included in our sustainability reports</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                4
              </span>
              <span>You'll receive a confirmation email shortly</span>
            </li>
          </ol>
        </div>

        {/* Partnership Message */}
        <div className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Users className="text-purple-600" size={20} />
            <h4 className="font-semibold text-gray-900">Partnership in Sustainability</h4>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Thank you for your partnership in our sustainability journey. Your data helps us understand and improve our environmental impact across the value chain.
          </p>
          <p className="text-xs text-gray-600 flex items-center justify-center gap-1">
            <Mail size={12} />
            Questions? Contact us at{' '}
            <a href="mailto:sustainability@company.com" className="text-purple-600 hover:text-purple-700 underline">
              sustainability@company.com
            </a>
          </p>
        </div>

        {/* Data Security Notice */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500">
          <Shield size={12} />
          <span>Your data is encrypted and stored securely</span>
        </div>

        {/* Close Button */}
        <button
          onClick={() => window.close()}
          className="mt-6 px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Close Window
        </button>
      </div>
    </div>
  );
}
